/**
 * Hard proof, read in the browser, that a program is driving it. Owner,
 * 2026-11-04: a browser carrying either one is a robot, not a person.
 *
 *   webdriver    `navigator.webdriver === true`. Set by Selenium, Playwright,
 *                Puppeteer and every WebDriver-driven browser. A person's own
 *                Chrome, Safari or Firefox never sets it.
 *   ua_mismatch  the user agent claims Mac or Windows while
 *                `navigator.platform` says Linux. 459 of 462 Stripe load
 *                failures in October were one bot doing exactly this
 *                (utils/stripe-loader.ts). Android says Linux in both, and an
 *                iPad in desktop mode says Mac in both, so neither trips it.
 *
 * Both ride on the `script_health` event (utils/script-health.ts), which every
 * hard page load sends whether or not reCAPTCHA ever scores the session. No
 * reCAPTCHA call is spent on this. Read back in utils/clickhouse/people-sql.ts.
 */

export interface RobotProof {
  webdriver: '0' | '1';
  ua_mismatch: '0' | '1';
}

type NavigatorLike = Pick<Navigator, 'userAgent' | 'platform'> & { webdriver?: boolean };

/** True when a driven browser says so on `navigator.webdriver`. */
export function isWebdriver(nav: NavigatorLike = navigator): boolean {
  return nav.webdriver === true;
}

/** True when the agent says Mac or Windows and the platform says Linux. */
export function isAgentMismatch(nav: NavigatorLike = navigator): boolean {
  return /Macintosh|Windows/.test(nav.userAgent || '') && /Linux/.test(nav.platform || '');
}

/** Both signals, as the event's string properties. */
export function readRobotProof(nav: NavigatorLike = navigator): RobotProof {
  return {
    webdriver: isWebdriver(nav) ? '1' : '0',
    ua_mismatch: isAgentMismatch(nav) ? '1' : '0',
  };
}
